import { useCallback, useMemo } from 'react';
import { useAppSelector } from './redux';

const EMPTY_PERMISSIONS: string[] = [];

export interface Permissions {
  /** True when the logged-in user is a subuser (parentUserId set). */
  isSubuser: boolean;
  /**
   * True when the user may perform the action gated by `key`.
   * Owner accounts (parentUserId === null) always pass: their empty
   * `permissions` array means "full access", not "no rights" - the backend
   * bypasses the permission check for them and the UI mirrors that.
   */
  can: (key: string) => boolean;
}

export function usePermissions(): Permissions {
  const permissions = useAppSelector(
    (state) => state.profile.contact?.permissions ?? EMPTY_PERMISSIONS
  );
  // null = owner account, string = subuser, undefined = contact not yet loaded
  const parentUserId = useAppSelector(
    (state) => state.profile.contact?.parentUserId
  );

  const isSubuser = typeof parentUserId === 'string';

  const can = useCallback(
    (key: string) => !isSubuser || permissions.includes(key),
    [isSubuser, permissions]
  );

  return useMemo(() => ({ isSubuser, can }), [isSubuser, can]);
}
